import { useState, useCallback, useMemo, useEffect } from 'react';
import { usePaymentQuery } from './usePaymentQuery';
import { getBasisTheoryKeys } from '../../../config/basisTheory';
import { useTagadaContext } from '../providers/TagadaProvider';
export function useGooglePayCheckout({ checkout, onSuccess, onError, onCancel, googlePayConfig, }) {
    const [processingPayment, setProcessingPayment] = useState(false);
    const [error, setError] = useState(null);
    const [paymentMethodConfig, setPaymentMethodConfig] = useState(null);
    const { processGooglePayPayment } = usePaymentQuery();
    const { apiService } = useTagadaContext();
    // Determine Basis Theory keys based on sandboxed flag from payment method config
    // When sandboxed is true (even on production hostname), use test keys
    const { basistheoryPublicKey, basistheoryTenantId } = useMemo(() => {
        const config = googlePayConfig || paymentMethodConfig;
        // Use test keys if sandboxed is true, production keys only when explicitly sandboxed: false
        const useProductionKeys = config?.sandboxed === false;
        const keys = getBasisTheoryKeys(useProductionKeys);
        console.log('[useGooglePayCheckout] Using Basis Theory keys:', {
            sandboxed: config?.sandboxed,
            useProductionKeys,
            tenantId: keys.tenantId,
        });
        return {
            basistheoryPublicKey: keys.apiKey,
            basistheoryTenantId: keys.tenantId,
        };
    }, [googlePayConfig, paymentMethodConfig]);
    // Fetch Google Pay payment method configuration
    useEffect(() => {
        if (!checkout?.checkoutSession?.id || googlePayConfig)
            return;
        const fetchPaymentMethods = async () => {
            try {
                const data = await apiService.fetch('/api/v1/payment-methods', {
                    method: 'GET',
                    params: { checkoutSessionId: checkout.checkoutSession.id },
                });
                const googlePayMethod = data?.find((method) => method.type === 'google_pay');
                if (googlePayMethod) {
                    setPaymentMethodConfig(googlePayMethod.metadata);
                }
                console.log('googlePayMethod', googlePayMethod);
            }
            catch (err) {
                console.error('Failed to fetch payment methods:', err);
            }
        };
        void fetchPaymentMethods();
    }, [checkout?.checkoutSession?.id, googlePayConfig, apiService]);
    // Tokenize Google Pay payment using Basis Theory
    const tokenizeGooglePay = useCallback(async (paymentData) => {
        try {
            // Extract the Google Pay token from the payment data
            const googlePayTokenString = paymentData.paymentMethodData.tokenizationData.token;
            const googlePayToken = JSON.parse(googlePayTokenString);
            const response = await fetch('https://api.basistheory.com/google-pay', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'BT-API-KEY': basistheoryPublicKey,
                },
                body: JSON.stringify({
                    google_payment_data: googlePayToken,
                }),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const result = await response.json();
            return result?.token_intent || result?.google_pay;
        }
        catch (err) {
            console.error('Tokenization failed:', err);
            throw err;
        }
    }, [basistheoryPublicKey]);
    // Handle Google Pay payment click
    const handleGooglePayClick = useCallback(() => {
        // Don't proceed if checkout is not available
        if (!checkout) {
            console.error('Checkout data not available');
            if (onError) {
                onError('Checkout not ready');
            }
            return;
        }
        setProcessingPayment(true);
        // Create Google Pay payment request (simpler than express - no shipping)
        const paymentRequest = {
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [
                {
                    type: 'CARD',
                    parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
                        billingAddressRequired: false,
                    },
                    tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                            gateway: 'basistheory',
                            gatewayMerchantId: basistheoryTenantId,
                        },
                    },
                },
            ],
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPrice: (checkout.summary.totalAdjustedAmount / 100).toFixed(2),
                currencyCode: checkout.summary.currency,
            },
            merchantInfo: {
                merchantName: googlePayConfig?.merchantName || paymentMethodConfig?.merchantName || checkout.checkoutSession?.store?.name || 'Store',
                merchantId: (() => {
                    const config = googlePayConfig || paymentMethodConfig;
                    if (!config)
                        return '12345678901234567890'; // Fallback to test
                    return config.sandboxed ? '12345678901234567890' : config.merchantId;
                })(),
            },
        };
        // Determine environment from configuration
        const config = googlePayConfig || paymentMethodConfig;
        const environment = config?.sandboxed !== false ? 'TEST' : 'PRODUCTION';
        // Create Google Pay client
        const paymentsClient = new google.payments.api.PaymentsClient({ environment });
        // Load payment data
        paymentsClient
            .loadPaymentData(paymentRequest)
            .then(async (paymentData) => {
            try {
                // Tokenize payment
                const googlePayToken = await tokenizeGooglePay(paymentData);
                // Process payment via SDK hook
                const result = await processGooglePayPayment(checkout.checkoutSession.id, googlePayToken, {
                    onPaymentSuccess: (response) => {
                        // Keep processing state true during navigation
                    },
                    onPaymentFailed: (err) => {
                        setProcessingPayment(false);
                        setError(err.message);
                        if (onError) {
                            onError(err.message);
                        }
                    },
                });
                // Call success callback
                if (onSuccess) {
                    onSuccess(result);
                }
            }
            catch (error) {
                console.error('Payment failed:', error);
                setProcessingPayment(false);
                const errorMsg = error instanceof Error ? error.message : 'Payment failed';
                setError(errorMsg);
                if (onError) {
                    onError(errorMsg);
                }
            }
        })
            .catch((error) => {
            console.error('Google Pay error:', error);
            setProcessingPayment(false);
            // Check if user canceled
            if (error.statusCode === 'CANCELED') {
                if (onCancel) {
                    onCancel();
                }
                return;
            }
            const errorMsg = error.statusMessage || 'Google Pay failed';
            setError(errorMsg);
            if (onError) {
                onError(errorMsg);
            }
        });
    }, [
        checkout,
        basistheoryTenantId,
        tokenizeGooglePay,
        processGooglePayPayment,
        onSuccess,
        onError,
        onCancel,
    ]);
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    return {
        handleGooglePayClick,
        processingPayment,
        error,
        clearError,
    };
}
